import { useEffect, useRef } from 'react'
import { Button } from '@/components/ui/Button'
import { useAuth } from '@/lib/auth/use-auth'

/**
 * Warn before the idle timeout signs the user out.
 *
 * Shared ops workstations mean sessions must close themselves, but dropping
 * someone mid-event would be dangerous — hence the warning.
 */
export function IdleWarningDialog() {
  const { idleWarningMs, keepAlive, signOut } = useAuth()
  const dialogRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (idleWarningMs === null) return
    dialogRef.current?.focus()
  }, [idleWarningMs])

  if (idleWarningMs === null) return null

  const seconds = Math.max(0, Math.ceil(idleWarningMs / 1000))
  const mm = String(Math.floor(seconds / 60)).padStart(2, '0')
  const ss = String(seconds % 60).padStart(2, '0')

  return (
    <div className="fixed inset-0 z-100 grid place-items-center bg-surface-dark/50 p-6">
      <div
        ref={dialogRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="idle-title"
        tabIndex={-1}
        className="w-full max-w-[440px] rounded-[24px] bg-canvas p-8 outline-none"
      >
        <h2 id="idle-title" className="t-title-lg text-ink">
          Session ending
        </h2>
        <p className="t-body-md mt-3 text-body">
          Signing out in{' '}
          <span className="t-number text-ink">
            {mm}:{ss}
          </span>
        </p>

        <div className="mt-8 flex gap-3">
          <Button onClick={keepAlive} fullWidth>
            Stay signed in
          </Button>
          <Button variant="secondary" onClick={() => void signOut('user')} fullWidth>
            Sign out
          </Button>
        </div>
      </div>
    </div>
  )
}
